import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  Video, 
  Loader2,
  ExternalLink,
  Trash2,
  FileText,
  Building2,
  Search,
  MessageSquare,
  RefreshCcw,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Paperclip,
  Download,
  UserPlus,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const meetingStatuses = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const Meetings = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [clients, setClients] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDate, setMeetingDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Meeting detail state
  const [comments, setComments] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('10:00');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  
  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  
  // Participants state
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [availableParticipants, setAvailableParticipants] = useState({ team: [], client: [] });
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Pending attachments for new meeting creation
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const createFileInputRef = useRef(null);
  
  const [meetingForm, setMeetingForm] = useState({
    organization_id: '',
    client_id: '',
    title: '',
    description: '',
    time: '10:00',
    duration_minutes: 30,
    send_email: true
  });
  
  // Loading state for submit button
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Bulk delete state
  const [selectedMeetings, setSelectedMeetings] = useState([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isClientTeam = user?.role === 'client_team';

  // Quick schedule helpers
  const setQuickSchedule = (minutesFromNow) => {
    const now = new Date();
    const scheduledTime = new Date(now.getTime() + minutesFromNow * 60000);
    setMeetingDate(scheduledTime);
    setMeetingForm(prev => ({
      ...prev,
      time: `${String(scheduledTime.getHours()).padStart(2, '0')}:${String(scheduledTime.getMinutes()).padStart(2, '0')}`
    }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [meetingsRes, tenantsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/meetings`, getAuthHeaders()),
        axios.get(`${API_URL}/tenants`, getAuthHeaders()),
        axios.get(`${API_URL}/clients`, getAuthHeaders())
      ]);
      setMeetings(meetingsRes.data || []);
      setTenants(tenantsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchMeetingDetails = async (meetingId) => {
    try {
      const [commentsRes, requestsRes, attachmentsRes] = await Promise.all([
        axios.get(`${API_URL}/meetings/${meetingId}/comments`, getAuthHeaders()),
        axios.get(`${API_URL}/meetings/${meetingId}/reschedule-requests`, getAuthHeaders()),
        axios.get(`${API_URL}/meetings/${meetingId}/attachments`, getAuthHeaders())
      ]);
      setComments(commentsRes.data || []);
      setRescheduleRequests(requestsRes.data || []);
      setAttachments(attachmentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch meeting details:', error);
    }
  };

  const handleOpenMeetingDetail = async (meeting) => {
    setSelectedMeeting(meeting);
    setIsDetailOpen(true);
    await fetchMeetingDetails(meeting.id);
  };

  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    if (!meetingDate) {
      toast.error('Please select a date');
      return;
    }
    if (!meetingForm.client_id || meetingForm.client_id === 'none') {
      toast.error('Please select a client');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const scheduledAt = new Date(meetingDate);
      const [hours, minutes] = meetingForm.time.split(':');
      scheduledAt.setHours(parseInt(hours), parseInt(minutes));
      
      const response = await axios.post(`${API_URL}/meetings`, {
        client_id: meetingForm.client_id,
        title: meetingForm.title,
        description: meetingForm.description,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: meetingForm.duration_minutes,
        send_email: meetingForm.send_email
      }, getAuthHeaders());
      
      const newMeetingId = response.data.id;
      
      // Upload pending attachments if any
      if (pendingAttachments.length > 0) {
        for (const file of pendingAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(
              `${API_URL}/meetings/${newMeetingId}/attachments`,
              formData,
              {
                ...getAuthHeaders(),
                headers: {
                  ...getAuthHeaders().headers,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (err) {
            console.error('Failed to upload attachment:', file.name);
          }
        }
        toast.success(`✅ Meeting "${meetingForm.title}" scheduled with ${pendingAttachments.length} attachment(s)!`, {
          duration: 5000,
        });
      } else {
        toast.success(`✅ Meeting "${meetingForm.title}" scheduled successfully!`, {
          duration: 5000,
        });
      }
      
      setIsCreateOpen(false);
      resetForm();
      setPendingAttachments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding pending attachment during meeting creation
  const handleAddPendingAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large (max 25MB)');
      return;
    }
    
    // Check for allowed types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/zip',
      'application/x-zip-compressed',
      'text/plain',
      'text/csv',
      'image/png',
      'image/jpeg',
      'image/gif'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed');
      return;
    }
    
    setPendingAttachments(prev => [...prev, file]);
    if (createFileInputRef.current) createFileInputRef.current.value = '';
  };

  const handleRemovePendingAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedMeeting) return;
    
    setCommentLoading(true);
    try {
      await axios.post(`${API_URL}/meetings/${selectedMeeting.id}/comments`, {
        content: newComment
      }, getAuthHeaders());
      toast.success('Comment added');
      setNewComment('');
      await fetchMeetingDetails(selectedMeeting.id);
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleRequestReschedule = async () => {
    if (!rescheduleDate || !selectedMeeting) {
      toast.error('Please select a new date');
      return;
    }
    
    setRescheduleLoading(true);
    try {
      const requestedDate = new Date(rescheduleDate);
      const [hours, minutes] = rescheduleTime.split(':');
      requestedDate.setHours(parseInt(hours), parseInt(minutes));
      
      await axios.post(`${API_URL}/meetings/${selectedMeeting.id}/reschedule-requests`, {
        requested_date: requestedDate.toISOString(),
        reason: rescheduleReason
      }, getAuthHeaders());
      
      toast.success('Reschedule request submitted');
      setIsRescheduleOpen(false);
      setRescheduleDate(null);
      setRescheduleTime('10:00');
      setRescheduleReason('');
      await fetchMeetingDetails(selectedMeeting.id);
    } catch (error) {
      toast.error('Failed to submit reschedule request');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleRespondToReschedule = async (requestId, action) => {
    try {
      await axios.put(
        `${API_URL}/meetings/${selectedMeeting.id}/reschedule-requests/${requestId}`,
        { action },
        getAuthHeaders()
      );
      toast.success(`Request ${action}d`);
      await fetchMeetingDetails(selectedMeeting.id);
      fetchData(); // Refresh meetings list if approved
    } catch (error) {
      toast.error('Failed to respond to request');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await axios.delete(`${API_URL}/meetings/${meetingId}`, getAuthHeaders());
      toast.success('Meeting deleted');
      setIsDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  // Bulk delete handlers
  const toggleMeetingSelection = (meetingId, e) => {
    e.stopPropagation();
    setSelectedMeetings(prev => 
      prev.includes(meetingId) 
        ? prev.filter(id => id !== meetingId)
        : [...prev, meetingId]
    );
  };

  const toggleSelectAll = (meetingsList) => {
    const allIds = meetingsList.map(m => m.id);
    const allSelected = allIds.every(id => selectedMeetings.includes(id));
    
    if (allSelected) {
      setSelectedMeetings(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedMeetings(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMeetings.length === 0) return;
    
    setIsBulkDeleting(true);
    try {
      const response = await axios.post(`${API_URL}/meetings/bulk-delete`, {
        meeting_ids: selectedMeetings
      }, getAuthHeaders());
      
      toast.success(`Successfully deleted ${response.data.deleted_count} meeting(s)`);
      setSelectedMeetings([]);
      setIsBulkDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete meetings');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleUpdateMeetingStatus = async (status) => {
    if (!selectedMeeting) return;
    try {
      await axios.put(`${API_URL}/meetings/${selectedMeeting.id}`, { status }, getAuthHeaders());
      toast.success('Meeting status updated');
      setSelectedMeeting({ ...selectedMeeting, status });
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setMeetingForm({
      organization_id: '',
      client_id: '',
      title: '',
      description: '',
      time: '10:00',
      duration_minutes: 30,
      send_email: true
    });
    setMeetingDate(null);
  };

  // Attachment functions
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedMeeting) return;
    
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error('File too large (max 25MB)');
      return;
    }
    
    setUploadingAttachment(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API_URL}/meetings/${selectedMeeting.id}/attachments`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setAttachments(prev => [...prev, res.data]);
      toast.success('Attachment uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${API_URL}/meetings/${selectedMeeting.id}/attachments/${attachment.id}/download`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(
        `${API_URL}/meetings/${selectedMeeting.id}/attachments/${attachmentId}`,
        getAuthHeaders()
      );
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  // Participant invite functions
  const handleOpenInviteModal = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/meetings/${selectedMeeting.id}/available-participants`,
        getAuthHeaders()
      );
      setAvailableParticipants(res.data);
      setSelectedParticipants([]);
      setIsInviteOpen(true);
    } catch (error) {
      toast.error('Failed to load available participants');
    }
  };

  const toggleParticipant = (userId) => {
    setSelectedParticipants(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleInviteParticipants = async () => {
    if (selectedParticipants.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }
    
    setInviteLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/meetings/${selectedMeeting.id}/participants`,
        {
          user_ids: selectedParticipants,
          send_email: true
        },
        getAuthHeaders()
      );
      toast.success(res.data.message);
      setIsInviteOpen(false);
      // Refresh meeting to get updated participants
      const meetingRes = await axios.get(`${API_URL}/meetings/${selectedMeeting.id}`, getAuthHeaders());
      setSelectedMeeting(meetingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite participants');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveParticipant = async (userId) => {
    try {
      await axios.delete(
        `${API_URL}/meetings/${selectedMeeting.id}/participants/${userId}`,
        getAuthHeaders()
      );
      setSelectedMeeting(prev => ({
        ...prev,
        participants: prev.participants?.filter(p => p.user_id !== userId) || []
      }));
      toast.success('Participant removed');
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return '📊';
    if (contentType?.includes('powerpoint') || contentType?.includes('presentation')) return '📽️';
    if (contentType?.includes('zip')) return '📦';
    if (contentType?.includes('image')) return '🖼️';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTenantName = (orgId) => {
    const tenant = tenants.find(t => t.id === orgId);
    return tenant?.company_name || tenant?.name || 'Unknown';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company || client?.name || '';
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesStatus = statusFilter === 'all' || meeting.status === statusFilter;
    const matchesSearch = searchQuery === '' || 
      meeting.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getTenantName(meeting.organization_id)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getClientName(meeting.client_id)?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const upcomingMeetings = filteredMeetings.filter(m => m.status === 'scheduled' && new Date(m.scheduled_at) >= new Date());
  const pastMeetings = filteredMeetings.filter(m => m.status !== 'scheduled' || new Date(m.scheduled_at) < new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Meeting Scheduler
          </h1>
          <p className="text-slate-500 mt-1">Schedule and manage meetings with tenants and clients</p>
        </div>
        {isAdmin && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="schedule-meeting-btn">
                <Plus size={18} className="mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Schedule a Meeting</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleScheduleMeeting} className="space-y-4 mt-4">
                {/* Tenant Selection */}
                <div className="space-y-2">
                  <Label>Tenant *</Label>
                  <Select
                    value={meetingForm.organization_id}
                    onValueChange={(value) => setMeetingForm({ ...meetingForm, organization_id: value, client_id: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a tenant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map(tenant => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.company_name || tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Client Selection */}
                {meetingForm.organization_id && (
                  <div className="space-y-2">
                    <Label>Client (Optional)</Label>
                    <Select
                      value={meetingForm.client_id || "none"}
                      onValueChange={(value) => setMeetingForm({ ...meetingForm, client_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client *" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.filter(c => c.organization_id === meetingForm.organization_id).map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.company || client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Client is required to schedule a meeting</p>
                  </div>
                )}

                {/* Title */}
                <div className="space-y-2">
                  <Label>Meeting Title *</Label>
                  <Input
                    value={meetingForm.title}
                    onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                    placeholder="e.g., Project Kickoff, Weekly Check-in"
                    required
                  />
                </div>

                {/* Date & Time */}
                {/* Quick Schedule Buttons */}
                <div className="space-y-2">
                  <Label>Quick Schedule</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQuickSchedule(5)}
                      className="text-xs"
                    >
                      In 5 min
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQuickSchedule(15)}
                      className="text-xs"
                    >
                      In 15 min
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQuickSchedule(30)}
                      className="text-xs"
                    >
                      In 30 min
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQuickSchedule(60)}
                      className="text-xs"
                    >
                      In 1 hour
                    </Button>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon size={16} className="mr-2" />
                          {meetingDate ? format(meetingDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={meetingDate}
                          onSelect={setMeetingDate}
                          initialFocus
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={meetingForm.time}
                      onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Duration */}
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select
                    value={meetingForm.duration_minutes.toString()}
                    onValueChange={(value) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                    placeholder="Meeting agenda or notes..."
                    rows={2}
                  />
                </div>

                {/* Attachments */}
                <div className="space-y-2">
                  <Label>Attachments</Label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      ref={createFileInputRef}
                      onChange={handleAddPendingAttachment}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv,.png,.jpg,.jpeg,.gif"
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => createFileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Paperclip size={16} className="mr-2" />
                      Add Attachment
                    </Button>
                    <p className="text-xs text-slate-500 text-center">PDF, Word, Excel, ZIP, Images (max 25MB each)</p>
                    
                    {/* Pending attachments list */}
                    {pendingAttachments.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {pendingAttachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                            <div className="flex items-center gap-2">
                              <span>{getFileIcon(file.type)}</span>
                              <div>
                                <p className="text-sm font-medium text-slate-900 truncate max-w-[180px]">{file.name}</p>
                                <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <Button 
                              type="button"
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                              onClick={() => handleRemovePendingAttachment(index)}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Send Email */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Send Email Invite</p>
                    <p className="text-sm text-slate-500">Notify attendees via email</p>
                  </div>
                  <Switch
                    checked={meetingForm.send_email}
                    onCheckedChange={(checked) => setMeetingForm({ ...meetingForm, send_email: checked })}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); setPendingAttachments([]); }} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      'Schedule Meeting'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search meetings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {meetingStatuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Bulk Delete Button */}
            {isAdmin && selectedMeetings.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setIsBulkDeleteOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete ({selectedMeetings.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Upcoming Meetings</h2>
            {isAdmin && upcomingMeetings.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => toggleSelectAll(upcomingMeetings)}
                className="text-slate-600"
              >
                {upcomingMeetings.every(m => selectedMeetings.includes(m.id)) ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
          <div className="grid gap-4">
            {upcomingMeetings.map(meeting => (
              <Card 
                key={meeting.id} 
                className={`border-slate-200 hover:shadow-md transition-shadow cursor-pointer ${selectedMeetings.includes(meeting.id) ? 'ring-2 ring-blue-500 bg-blue-50/50' : ''}`}
                onClick={() => handleOpenMeetingDetail(meeting)}
                data-testid={`meeting-card-${meeting.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {/* Checkbox for selection */}
                      {isAdmin && (
                        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedMeetings.includes(meeting.id)}
                            onCheckedChange={() => toggleMeetingSelection(meeting.id, { stopPropagation: () => {} })}
                            data-testid={`meeting-checkbox-${meeting.id}`}
                          />
                        </div>
                      )}
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <Video className="text-blue-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{meeting.title}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {getTenantName(meeting.organization_id)}
                          </span>
                          {meeting.client_id && (
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {getClientName(meeting.client_id)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-slate-600">
                            <CalendarIcon size={14} />
                            {format(new Date(meeting.scheduled_at), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1 text-slate-600">
                            <Clock size={14} />
                            {format(new Date(meeting.scheduled_at), 'h:mm a')}
                          </span>
                          <span className="text-slate-400">({meeting.duration_minutes} min)</span>
                        </div>
                        {meeting.description && (
                          <p className="text-sm text-slate-500 mt-2 line-clamp-1">{meeting.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {meeting.room_name && meeting.status === 'scheduled' && (
                        <a href={`https://meet.saysme.org/${meeting.room_name}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                            <Video size={14} className="mr-2" />
                            Join
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Past/Other Meetings */}
      {pastMeetings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Past & Other Meetings</h2>
            {isAdmin && pastMeetings.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => toggleSelectAll(pastMeetings)}
                className="text-slate-600"
              >
                {pastMeetings.every(m => selectedMeetings.includes(m.id)) ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    {isAdmin && <th className="w-12"></th>}
                    <th>Title</th>
                    <th>Tenant</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pastMeetings.map(meeting => (
                    <tr 
                      key={meeting.id} 
                      className={`cursor-pointer hover:bg-slate-50 ${selectedMeetings.includes(meeting.id) ? 'bg-blue-50' : ''}`}
                      onClick={() => handleOpenMeetingDetail(meeting)}
                    >
                      {isAdmin && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedMeetings.includes(meeting.id)}
                            onCheckedChange={() => toggleMeetingSelection(meeting.id, { stopPropagation: () => {} })}
                            data-testid={`meeting-checkbox-past-${meeting.id}`}
                          />
                        </td>
                      )}
                      <td className="font-medium">{meeting.title}</td>
                      <td>{getTenantName(meeting.organization_id)}</td>
                      <td>{meeting.client_id ? getClientName(meeting.client_id) : '-'}</td>
                      <td>{format(new Date(meeting.scheduled_at), 'MMM d, yyyy h:mm a')}</td>
                      <td>
                        <span className={`status-badge status-${meeting.status}`}>
                          {meetingStatuses.find(s => s.value === meeting.status)?.label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {filteredMeetings.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <CalendarIcon size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No meetings found</h3>
            <p className="text-slate-500 mt-1">Schedule your first meeting to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Meeting Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Video className="text-blue-600" size={24} />
              {selectedMeeting?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMeeting && (
            <div className="flex-1 overflow-y-auto">
              {/* Meeting Info Section */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Tenant</p>
                    <p className="font-medium flex items-center gap-2">
                      <Building2 size={14} className="text-slate-400" />
                      {getTenantName(selectedMeeting.organization_id)}
                    </p>
                  </div>
                  {selectedMeeting.client_id && (
                    <div>
                      <p className="text-sm text-slate-500">Client</p>
                      <p className="font-medium flex items-center gap-2">
                        <Users size={14} className="text-slate-400" />
                        {getClientName(selectedMeeting.client_id)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-500">Date & Time</p>
                    <p className="font-medium flex items-center gap-2">
                      <CalendarIcon size={14} className="text-slate-400" />
                      {format(new Date(selectedMeeting.scheduled_at), 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-slate-600 ml-5">
                      {format(new Date(selectedMeeting.scheduled_at), 'h:mm a')} ({selectedMeeting.duration_minutes} min)
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <span className={`status-badge status-${selectedMeeting.status}`}>
                      {meetingStatuses.find(s => s.value === selectedMeeting.status)?.label}
                    </span>
                  </div>
                </div>
                {selectedMeeting.description && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Description</p>
                    <p className="text-slate-700">{selectedMeeting.description}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedMeeting.room_name && selectedMeeting.status === 'scheduled' && (
                  <a href={`https://meet.saysme.org/${selectedMeeting.room_name}`} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <Video size={16} className="mr-2" />
                      Join Meeting
                    </Button>
                  </a>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => setIsRescheduleOpen(true)}
                  disabled={selectedMeeting.status !== 'scheduled'}
                >
                  <RefreshCcw size={16} className="mr-2" />
                  Request Reschedule
                </Button>
                {isAdmin && (
                  <>
                    <Select 
                      value={selectedMeeting.status} 
                      onValueChange={handleUpdateMeetingStatus}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {meetingStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="ghost" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteMeeting(selectedMeeting.id)}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  </>
                )}
              </div>

              {/* Tabs for Comments, Attachments, Participants, and Reschedule Requests */}
              <Tabs defaultValue="comments" className="mt-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="comments" className="flex items-center gap-2 text-xs">
                    <MessageSquare size={14} />
                    Comments ({comments.length})
                  </TabsTrigger>
                  <TabsTrigger value="attachments" className="flex items-center gap-2 text-xs">
                    <Paperclip size={14} />
                    Files ({attachments.length})
                  </TabsTrigger>
                  <TabsTrigger value="participants" className="flex items-center gap-2 text-xs">
                    <Users size={14} />
                    Team ({selectedMeeting?.participants?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="reschedule" className="flex items-center gap-2 text-xs">
                    <RefreshCcw size={14} />
                    Reschedule ({rescheduleRequests.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="comments" className="mt-4 space-y-4">
                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment or note before the meeting..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleAddComment} 
                      disabled={commentLoading || !newComment.trim()}
                      className="bg-slate-900 hover:bg-slate-800"
                    >
                      {commentLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                  
                  {/* Comments List */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {comments.length === 0 ? (
                      <p className="text-center text-slate-400 py-4">No comments yet. Add one above!</p>
                    ) : (
                      comments.map(comment => (
                        <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-medium text-slate-900 text-sm">{comment.user_name}</p>
                            <p className="text-xs text-slate-400">
                              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                            </p>
                          </div>
                          <p className="text-slate-700 text-sm">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                {/* Attachments Tab */}
                <TabsContent value="attachments" className="mt-4 space-y-4">
                  {/* Upload Button */}
                  {!isClientTeam && (
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleAttachmentUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv,.png,.jpg,.jpeg,.gif"
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                      >
                        {uploadingAttachment ? (
                          <Loader2 size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Paperclip size={16} className="mr-2" />
                        )}
                        Add Attachment
                      </Button>
                      <span className="text-xs text-slate-500">PDF, Word, Excel, ZIP (max 25MB)</span>
                    </div>
                  )}
                  
                  {/* Attachments List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {attachments.length === 0 ? (
                      <p className="text-center text-slate-400 py-4">No attachments. Add files to share with participants.</p>
                    ) : (
                      attachments.map(attachment => (
                        <div key={attachment.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(attachment.content_type)}</span>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{attachment.filename}</p>
                              <p className="text-xs text-slate-500">{formatFileSize(attachment.size)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDownloadAttachment(attachment)}
                            >
                              <Download size={14} />
                            </Button>
                            {!isClientTeam && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                {/* Participants Tab */}
                <TabsContent value="participants" className="mt-4 space-y-4">
                  {/* Invite Button */}
                  {!isClientTeam && (
                    <Button variant="outline" onClick={handleOpenInviteModal}>
                      <UserPlus size={16} className="mr-2" />
                      Invite Team Members
                    </Button>
                  )}
                  
                  {/* Participants List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {/* Meeting Creator */}
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {selectedMeeting?.created_by_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{selectedMeeting?.created_by_name}</p>
                          <p className="text-xs text-slate-500">Organizer</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Additional Participants */}
                    {(selectedMeeting?.participants || []).map(participant => (
                      <div key={participant.user_id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-400 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {participant.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{participant.name}</p>
                            <p className="text-xs text-slate-500">{participant.email}</p>
                          </div>
                        </div>
                        {!isClientTeam && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveParticipant(participant.user_id)}
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {(!selectedMeeting?.participants || selectedMeeting.participants.length === 0) && (
                      <p className="text-center text-slate-400 py-2 text-sm">No additional participants invited yet.</p>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="reschedule" className="mt-4 space-y-3">
                  {rescheduleRequests.length === 0 ? (
                    <p className="text-center text-slate-400 py-4">No reschedule requests</p>
                  ) : (
                    rescheduleRequests.map(request => (
                      <div key={request.id} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{request.requested_by_name}</p>
                            <p className="text-sm text-slate-600">
                              Requested: {format(new Date(request.requested_date), 'MMM d, yyyy h:mm a')}
                            </p>
                            {request.reason && (
                              <p className="text-sm text-slate-500 mt-1">Reason: {request.reason}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {request.status === 'pending' ? (
                              isAdmin ? (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-emerald-600 border-emerald-600"
                                    onClick={() => handleRespondToReschedule(request.id, 'approve')}
                                  >
                                    <CheckCircle size={14} className="mr-1" />
                                    Approve
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-red-600 border-red-600"
                                    onClick={() => handleRespondToReschedule(request.id, 'reject')}
                                  >
                                    <XCircle size={14} className="mr-1" />
                                    Reject
                                  </Button>
                                </>
                              ) : (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                                  <AlertCircle size={12} />
                                  Pending
                                </span>
                              )
                            ) : (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                request.status === 'approved' 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {request.status === 'approved' ? '✓ Approved' : '✕ Rejected'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Request Modal */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Request Reschedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>New Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon size={16} className="mr-2" />
                    {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select new date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={setRescheduleDate}
                    initialFocus
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>New Time *</Label>
              <Input
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Reason (Optional)</Label>
              <Textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Why do you need to reschedule?"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsRescheduleOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleRequestReschedule} 
                disabled={rescheduleLoading || !rescheduleDate}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {rescheduleLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCcw size={16} className="mr-2" />}
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Participants Modal */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <UserPlus size={20} className="inline mr-2" />
              Invite Team Members
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Team Members */}
            {availableParticipants.team?.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Tenant Team</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableParticipants.team.map(user => (
                    <div 
                      key={user.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                        selectedParticipants.includes(user.id) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-slate-50 border-transparent hover:bg-slate-100'
                      }`}
                      onClick={() => toggleParticipant(user.id)}
                    >
                      <Checkbox checked={selectedParticipants.includes(user.id)} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Client Users */}
            {availableParticipants.client?.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Client Team</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {availableParticipants.client.map(user => (
                    <div 
                      key={user.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                        selectedParticipants.includes(user.id) 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-slate-50 border-transparent hover:bg-slate-100'
                      }`}
                      onClick={() => toggleParticipant(user.id)}
                    >
                      <Checkbox checked={selectedParticipants.includes(user.id)} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {user.role === 'client_team' ? 'team' : 'client'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {availableParticipants.team?.length === 0 && availableParticipants.client?.length === 0 && (
              <p className="text-center text-slate-400 py-4">No additional users available to invite.</p>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleInviteParticipants}
                disabled={inviteLoading || selectedParticipants.length === 0}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {inviteLoading ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Send size={16} className="mr-2" />
                )}
                Send Invites ({selectedParticipants.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete {selectedMeetings.length} Meeting{selectedMeetings.length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              <p className="mb-3">
                This action cannot be undone. The following will happen:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>All selected meetings will be permanently deleted</li>
                <li>Cancellation emails will be sent to participants</li>
                <li>Meeting comments and attachments will be removed</li>
              </ul>
              <p className="mt-3 font-medium">
                Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-bulk-delete-btn"
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete {selectedMeetings.length} Meeting{selectedMeetings.length !== 1 ? 's' : ''}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Meetings;
